import { useEffect, useState } from "react";
import { Copy, Users, Send, Trophy } from "lucide-react";
import { api, WHATSAPP_NUMBER } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AffiliateCard } from "@/components/AffiliateCard";
import { toast } from "sonner";

export default function ReferralPage() {
  const [data, setData] = useState(null);
  const [board, setBoard] = useState([]);
  const [contest, setContest] = useState(null);
  useEffect(() => { (async () => {
    try {
      const { data } = await api.get("/referrals");
      setData(data);
    } catch (err) {
      console.error("Failed to load referrals:", err);
    }
    try {
      const { data: lb } = await api.get("/referrals/leaderboard");
      setBoard(lb || []);
    } catch {}
    try {
      const { data: c } = await api.get("/referrals/contest");
      setContest(c);
    } catch {}
  })(); }, []);

  const link = data?.referral_code ? `${window.location.origin}/signup?ref=${data.referral_code}` : "";
  const copy = async () => { if (link) { await navigator.clipboard.writeText(link); toast.success("Link copied!"); } };
  const shareWa = () => {
    const msg = `Join Zuntria and earn rewards! Use my link: ${link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 md:px-10">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Referrals</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">Invite & Earn</h1>
      <p className="mt-2 text-muted-foreground">Share your link. When someone you invite upgrades to Pro, <strong className="text-emerald-400">you get 30 free Pro days per upgrade</strong>.</p>
      <p className="mt-1 text-xs text-muted-foreground" data-testid="referral-legal-note">Paid via PayPal. Zuntria is not a bank.</p>

      <div className="mt-8 rounded-2xl border border-border bg-card p-6" data-testid="credit-progress-card">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium">⚡ {Math.min(data?.zuntria_credits ?? 0, 200)} of 200 credits to a FREE Pro month</span>
          <span className="text-xs text-muted-foreground">{Math.max(200 - (data?.zuntria_credits ?? 0), 0)} to go</span>
        </div>
        <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-gradient-to-r from-amber-500 to-emerald-400 transition-all"
            style={{ width: `${Math.min(((data?.zuntria_credits ?? 0) / 200) * 100, 100)}%` }}
            data-testid="credit-progress-bar"
          />
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Earn credits from referrals, daily streaks & goals — then pay for Pro with credits on the Pricing page.</p>
      </div>

      <AffiliateCard />

      <div className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="referral-section">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your referral code</div>
        <div className="mt-2 font-display text-3xl text-primary" data-testid="referral-code">{data?.referral_code || "—"}</div>

        <div className="mt-6 text-xs uppercase tracking-[0.2em] text-muted-foreground">Your referral link</div>
        <div className="mt-2 flex items-center gap-2 rounded-md border border-border bg-background px-3 py-2.5">
          <div className="flex-1 truncate text-sm" data-testid="referral-link">{link || "—"}</div>
          <Button size="sm" variant="ghost" className="h-10 w-10 p-0" onClick={copy} data-testid="referral-copy"><Copy className="h-3.5 w-3.5" /></Button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <Button onClick={copy} data-testid="referral-copy-btn"><Copy className="mr-1 h-4 w-4" /> Copy link</Button>
          <Button onClick={shareWa} className="bg-[#25D366] text-white hover:bg-[#1ebd5b]" data-testid="referral-share-whatsapp"><Send className="mr-1 h-4 w-4" /> Share on WhatsApp</Button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-3">
          <Stat label="Zuntria Credits" value={data?.zuntria_credits ?? 0} />
          <Stat label="Points" value={data?.points ?? 0} />
          <Stat label="Total referrals" value={data?.referral_count ?? 0} />
        </div>
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
        <h2 className="font-display text-xl flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Friends you invited</h2>
        {!data?.referred_users?.length ? (
          <p className="mt-3 text-sm text-muted-foreground">No referrals yet — share your link to start earning.</p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {data.referred_users.map((u) => (
              <li key={`${u.name}-${u.created_at}`} className="flex items-center justify-between py-3 text-sm">
                <span>{u.name}</span>
                <span className="text-xs text-muted-foreground">{new Date(u.created_at).toLocaleDateString()}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {contest && (
        <div className="mt-8 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 via-card to-transparent p-6 md:p-8" data-testid="referral-contest-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-xl flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-400" /> {contest.month} Referral Contest</h2>
            <span className="rounded-full bg-amber-500/15 px-3 py-1 text-xs font-semibold text-amber-400" data-testid="contest-days-left">{contest.days_left} days left</span>
          </div>
          <p className="mt-2 text-sm">This month's top referrer wins <strong className="text-amber-400">{contest.prize}</strong> — awarded automatically on the 1st.</p>
          {contest.top?.length > 0 ? (
            <ul className="mt-4 divide-y divide-border">
              {contest.top.map((r) => (
                <li key={r.rank} className="flex items-center justify-between py-2.5 text-sm" data-testid={`contest-row-${r.rank}`}>
                  <span>{r.rank === 1 ? "🥇" : r.rank === 2 ? "🥈" : r.rank === 3 ? "🥉" : `${r.rank}.`} {r.name}</span>
                  <span className="text-xs text-muted-foreground">{r.referrals} this month</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">No entries yet this month — one referral could put you in 1st place 🏆</p>
          )}
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8" data-testid="referral-leaderboard">
        <h2 className="font-display text-xl flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-400" /> Top referrers</h2>
        {!board.length ? (
          <p className="mt-3 text-sm text-muted-foreground">No referrers yet — be the first on the board!</p>
        ) : (
          <ul className="mt-4 divide-y divide-border">
            {board.map((r) => (
              <li key={r.rank} className="flex items-center justify-between py-3 text-sm" data-testid={`leaderboard-row-${r.rank}`}>
                <span className="flex items-center gap-3">
                  <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                    r.rank === 1 ? "bg-amber-400/20 text-amber-400" :
                    r.rank === 2 ? "bg-zinc-400/20 text-zinc-300" :
                    r.rank === 3 ? "bg-orange-700/20 text-orange-400" :
                    "bg-secondary text-muted-foreground"}`}>{r.rank}</span>
                  {r.name}
                </span>
                <span className="text-xs text-muted-foreground">{r.referrals} referral{r.referrals === 1 ? "" : "s"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card p-6 md:p-8">
        <h2 className="font-display text-xl">How it works</h2>
        <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
          <li>• Invite a friend with your link</li>
          <li>• They sign up free</li>
          <li>• When they upgrade to Pro (£14.99/month), you get <strong className="text-emerald-400">30 extra Pro days</strong></li>
        </ul>
        <p className="mt-4 text-xs text-muted-foreground">Paid via PayPal. Zuntria is not a bank.</p>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
      <div className="mt-1 font-display text-lg">{value}</div>
    </div>
  );
}
